import { useState } from 'react';
import { Button, Group, MultiSelect, TextInput } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { endOfMonth, format, parseISO, startOfMonth } from 'date-fns';

import { IconSearch } from '@tabler/icons-react';
import {
  EtcTicketState,
  ETC_TICKET_STATE_POLICY,
  ETC_STATE_VALUE,
} from '#/components/ui/tickets/etc/model';
import type { DefaultOptionsParams } from './useTicketSearchBarParams';
import { useTicketSearchBarParams } from './useTicketSearchBarParams';

type YYYYMMDD = string;

const getStatesOption = (availStates: EtcTicketState[] = ETC_STATE_VALUE) => {
  return Object.entries(ETC_TICKET_STATE_POLICY)
    .filter(([key]) => {
      return availStates.includes(key as EtcTicketState);
    })
    .map(([value, { text }]) => {
      const STATE = value as EtcTicketState;

      return {
        value: STATE,
        label: text,
      };
    });
};

const getMonthRangeFromDate = (date: Date): [YYYYMMDD, YYYYMMDD] => {
  return [
    format(startOfMonth(date), 'yyyy-MM-dd'),
    format(endOfMonth(date), 'yyyy-MM-dd'),
  ];
};

const dateFromYYYYMMDD = (s: string | undefined): Date | null =>
  s ? parseISO(s) : null;

export type AvailTicketSearchBarField = {
  dateRange?: boolean;
  state?: boolean;
  search?: boolean;
};

export type TicketSearchBarProps = {
  defaultOptions: DefaultOptionsParams;
  availField?: AvailTicketSearchBarField;
  availStates?: EtcTicketState[];
};

export function TicketSearchBar({
  defaultOptions,
  availField,
  availStates,
}: TicketSearchBarProps) {
  const { params, setState, setFuzzySearchText, reset, setRange } =
    useTicketSearchBarParams(defaultOptions);
  const [inputSearchValue, setSearchInputValue] = useState(
    () => params.fuzzySearchText ?? '',
  );

  const statesOptions = getStatesOption(availStates);

  return (
    <Group gap="sm">
      {availField?.search !== false && (
        <TextInput
          flex={1}
          value={inputSearchValue}
          onChange={(e) => {
            setFuzzySearchText(e.target.value);
            setSearchInputValue(e.target.value);
          }}
          type="text"
          placeholder="搜尋 Email、車牌、證件號碼 / 統編"
          leftSection={<IconSearch size={16} />}
        />
      )}
      {availField?.state !== false && (
        <MultiSelect
          w={160}
          data={statesOptions}
          placeholder={params.state.length < 1 ? '全部' : undefined}
          clearable
          value={params.state ?? undefined}
          renderPill={({ option }) => {
            if (option.value !== params.state[0]) {
              return null;
            }

            if (params.state.length > 1) {
              return '...';
            }

            return option.label;
          }}
          onChange={(v) => {
            setState(v);
          }}
        />
      )}
      {availField?.dateRange !== false && (
        <MonthPickerInput
          valueFormat="YYYY-MM"
          placeholder="選擇月份"
          value={dateFromYYYYMMDD(params.fromDate)}
          onChange={(v) => {
            if (!v) {
              setRange(undefined);
            } else {
              const startYYYYMMDD = v;
              const [from, to] = getMonthRangeFromDate(new Date(startYYYYMMDD));

              setRange([from, to]);
            }
          }}
          clearable
          w={110}
        />
      )}
      <Button variant="default" onClick={reset}>
        重設
      </Button>
    </Group>
  );
}
